import type { ExpandableLegendSeries, ExpandableLegendTimestamp, VerticalLine } from '../types';

export interface CreateExpandableLegendOptions {
  focusMode: boolean;
  focusedSensors: Record<string, string>; // sectionId -> sensorName
  selectedTimestampsSet?: Set<string>; // Selected timestamp IDs (controls which sub-rows appear)
  visibleTimestampsSet?: Set<string>; // Visible timestamp IDs (controls chart data opacity)
  verticalLines: VerticalLine[]; // Available markers/timestamps
  sensorColors: Record<string, string>; // Sensor name -> color
  enabledSensors: Record<string, boolean>; // Sensor name -> enabled
  includeLatest?: boolean; // Whether to include "Latest" as a timestamp option
  timestampsLoading?: boolean; // Whether timestamps data is currently loading
}

/**
 * Creates expandable legend series from focus mode state.
 *
 * This is a generic utility that converts multi-timestamp focus mode state
 * into the ExpandableLegendSeries format used by the expandable legend UI.
 *
 * Use case: When a sensor is focused with multiple timestamps selected,
 * show the sensor as an expandable item with its timestamps as children.
 *
 * @param options - Configuration options
 * @returns Array of ExpandableLegendSeries for rendering
 */
export function createExpandableLegendFromFocus(
  options: CreateExpandableLegendOptions
): ExpandableLegendSeries[] {
  const {
    focusMode,
    focusedSensors,
    selectedTimestampsSet = new Set(),
    visibleTimestampsSet = new Set(),
    verticalLines,
    sensorColors,
    enabledSensors,
    includeLatest = true,
    timestampsLoading = false,
  } = options;

  // Only generate expandable series when in focus mode OR when there are focused sensors
  // This ensures that when a user clicks the eye icon to focus a sensor (even without entering multi-timestamp mode),
  // the expandable legend data is still created so the chart can display the focused sensor's data
  if (!focusMode && Object.keys(focusedSensors).length === 0) {
    return [];
  }

  const expandableSeries: ExpandableLegendSeries[] = [];

  // CRITICAL FIX: In focus mode with timestamps selected, create expandable series for ALL enabled sensors
  // This ensures timestamp filtering works correctly for all sections, not just the focused one
  const sensorsToProcess: Array<[string, string]> = [];

  if (focusMode && selectedTimestampsSet.size > 0) {
    // Focus mode with timestamps: Create expandable series for ALL enabled sensors
    Object.entries(enabledSensors).forEach(([sensorName, isEnabled]) => {
      if (isEnabled) {
        // Find which section this sensor belongs to (if it's focused, use that sectionId)
        const sectionId = Object.entries(focusedSensors).find(([_, name]) => name === sensorName)?.[0] || 'default';
        sensorsToProcess.push([sectionId, sensorName]);
      }
    });
  } else {
    // Normal focus mode (no timestamps or single timestamp): Only process focused sensors
    Object.entries(focusedSensors).forEach(([sectionId, sensorName]) => {
      if (sensorName && enabledSensors[sensorName]) {
        sensorsToProcess.push([sectionId, sensorName]);
      }
    });
  }

  // For each sensor to process, create an expandable series
  sensorsToProcess.forEach(([sectionId, sensorName]) => {
    if (!sensorName || !enabledSensors[sensorName]) {
      return;
    }

    // Sort vertical lines by timestamp (chronological order)
    const sortedLines = [...verticalLines].sort((a, b) => {
      const aTime = typeof a.timestamp === 'number' ? a.timestamp : a.timestamp.getTime();
      const bTime = typeof b.timestamp === 'number' ? b.timestamp : b.timestamp.getTime();
      return aTime - bTime;
    });

    // Create timestamp items from sorted vertical lines
    const timestamps: ExpandableLegendTimestamp[] = sortedLines
      .filter(line => !line.isHarmonic) // Only show user markers, not harmonics
      .map(line => {
        // Format timestamp label with date and time
        let formattedLabel = line.label;

        const timestampDate = typeof line.timestamp === 'number'
          ? new Date(line.timestamp)
          : line.timestamp;

        // Check if this is a real date (not just a numeric value)
        if (timestampDate instanceof Date && timestampDate.getFullYear() >= 1970) {
          const time = timestampDate.getTime();
          // Only format if it's a real timestamp (not a small numeric x-axis value)
          if (time >= 100000000000) {
            const formatter = new Intl.DateTimeFormat('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            const formatted = formatter.format(timestampDate);
            formattedLabel = `${line.label} - ${formatted}`;
          }
        }

        const timestampISO = typeof line.timestamp === 'number'
          ? new Date(line.timestamp).toISOString()
          : line.timestamp.toISOString();

        return {
          id: line.id,
          label: formattedLabel, // e.g., "T1 - Feb 13, 03:47"
          timestamp: timestampISO,
          enabled: selectedTimestampsSet.has(timestampISO) || selectedTimestampsSet.has(line.id),
          visible: visibleTimestampsSet.has(timestampISO) || visibleTimestampsSet.has(line.id),
          color: line.color, // Include marker color for chart series rendering
        };
      });

    // Add "Latest" as a timestamp option if enabled
    if (includeLatest) {
      // Check if any of the "Latest" variations are selected
      // IMPORTANT: Empty set = Latest selected by default (no explicit timestamps chosen)
      const latestEnabled =
        selectedTimestampsSet.size === 0 ||
        selectedTimestampsSet.has('__latest__') ||
        selectedTimestampsSet.has('latest') ||
        selectedTimestampsSet.has('0');

      const latestVisible =
        visibleTimestampsSet.size === 0 ||
        visibleTimestampsSet.has('__latest__') ||
        visibleTimestampsSet.has('latest') ||
        visibleTimestampsSet.has('0');

      timestamps.unshift({
        id: '__latest__',
        label: 'Latest',
        timestamp: new Date().toISOString(),
        enabled: latestEnabled,
        visible: latestVisible,
        color: sensorColors[sensorName] || '#6667dd', // Use sensor color or default
      });
    }

    // Check if this sensor is actually focused (not just enabled)
    const isActuallyFocused = Object.values(focusedSensors).includes(sensorName);

    const series = {
      id: `${sectionId}-${sensorName}`,
      label: sensorName,
      color: sensorColors[sensorName] || '#6667dd',
      isSelected: enabledSensors[sensorName],
      isFocused: isActuallyFocused, // Only mark as focused if it's actually in focusedSensors
      timestamps,
      timestampsLoading,
    };

    expandableSeries.push(series);
  });

  return expandableSeries;
}

/**
 * Toggle a timestamp on/off in the selectedTimestampsSet.
 *
 * @param selectedTimestampsSet - Current selected timestamps
 * @param timestampId - ID of the timestamp to toggle
 * @returns New Set with the timestamp toggled
 */
export function toggleTimestampInSet(
  selectedTimestampsSet: Set<string>,
  timestampId: string
): Set<string> {
  const newSet = new Set(selectedTimestampsSet);

  // Special handling for "Latest" - all variations must be toggled together
  if (timestampId === '__latest__' || timestampId === 'latest' || timestampId === '0') {
    const isEnabled = newSet.has('__latest__') || newSet.has('latest') || newSet.has('0');

    if (isEnabled) {
      // Remove all "Latest" variations
      newSet.delete('__latest__');
      newSet.delete('latest');
      newSet.delete('0');
    } else {
      // Add all "Latest" variations to ensure compatibility
      newSet.add('__latest__');
      newSet.add('latest');
      newSet.add('0');
    }
  } else {
    // Toggle regular timestamp
    if (newSet.has(timestampId)) {
      newSet.delete(timestampId);
    } else {
      newSet.add(timestampId);
    }
  }

  return newSet;
}
